import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Home, Mail, Lock, AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react';

import { getDefaultRouteForRole } from '../lib/roleContext';
import { getPostAuthRedirect } from '../lib/emailVerificationFlow';

function defaultRouteForRole(userData) {
  return getDefaultRouteForRole(userData?.role);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const { t } = useLanguage();
  const [email, setEmail] = useState(() => localStorage.getItem('rememberedEmail') || '');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem('rememberedEmail'));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const redirectTo = searchParams.get('redirect');

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const userData = await login(email, password, rememberMe);
      // Login is allowed for unverified users, but the verification gate takes
      // precedence over any deep-link redirect so they land on the pending page.
      const fallbackRoute = redirectTo || defaultRouteForRole(userData);
      navigate(getPostAuthRedirect(userData, fallbackRoute));
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-champagne-100 via-ivory to-ivory flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 bg-gradient-to-br from-signal-blue to-military-gold rounded-xl shadow-md flex items-center justify-center">
            <Home className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading text-2xl font-bold text-slate-800">
            TLA<span className="text-military-gold">HOUSING</span>
          </span>
        </Link>

        <div className="bg-white rounded-2xl shadow-[0_8px_40px_rgba(15,23,42,0.08)] border border-champagne-200 p-8">
          <div className="text-center mb-6">
            <h1 className="font-heading text-2xl font-bold text-slate-800 mb-2">{t('auth.welcome_back', 'WELCOME BACK')}</h1>
            <p className="text-slate-600 text-sm">{t('auth.sign_in_to_access', 'Sign in to access your account')}</p>
          </div>

          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-sm mb-6">
              <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700 text-sm">{t('auth.email_address', 'Email Address')}</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-signal-blue"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 text-sm">{t('auth.password', 'Password')}</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="••••••••"
                  required
                  className="pl-10 pr-10 bg-white border-slate-300 text-slate-800 placeholder:text-slate-400 focus:border-signal-blue"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700 transition-colors"
                  data-testid="toggle-password"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(event) => setRememberMe(event.target.checked)}
                  className="w-4 h-4 rounded border-slate-400 bg-white text-signal-blue focus:ring-signal-blue"
                  data-testid="remember-me"
                />
                <span className="text-sm text-slate-600">{t('auth.remember_me', 'Remember me')}</span>
              </label>
              <Link to="/forgot-password" className="text-sm text-slate-600 hover:text-signal-blue">
                {t('auth.forgot_password', 'Forgot password?')}
              </Link>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-signal-blue hover:bg-signal-blue-hover text-white rounded-lg py-5 shadow-md hover:shadow-lg transition-all"
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('auth.signing_in', 'Signing In...')}
                </>
              ) : (
                t('auth.login', 'Sign In')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-slate-600 text-sm">
              {t('auth.dont_have_account', "Don't have an account?")}{' '}
              <Link to="/register" className="text-signal-blue hover:text-signal-blue-hover" data-testid="register-link">
                Create one
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
